import React, { useEffect, useState } from "react";
import {
  Check,
  Copy,
  Download,
  Loader2,
  Pencil,
  X,
} from "lucide-react";
import { api } from "../../../../redux/apis/api";
import errorToast from "../../../../components/helper-components/error-toast";
import successToast from "../../../../components/helper-components/success-toast";
import { isAdmin } from "../../../../redux/config";

// Slide-in detail panel for a coupon row. Shows the at-a-glance dashboard the
// user requested:
//   - Header (name, status, created date, mode)
//   - KPI strip: for series → Generated / Issued / Redeemed
//                for single → Single-code / Used / Limit
//   - Progress bars (issued % of generated, redeemed % of issued)
//   - Configuration grid (code format, uses per code, discount, max cap,
//     min purchase, validity window, returnable)
//   - Sample codes section (series only) — first 3 codes + total
//   - Footer actions: Export CSV (series only) + Edit
//
// Reads the coupon row in from the list (no second fetch needed for the
// header) and lazy-loads code samples + counters via /api/coupons/:id/codes.

const fmtNum = (n) => Number(n || 0).toLocaleString("en-IN");
const fmtINR = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;
const fmtPct = (a, b) => {
  if (!b) return "0%";
  const v = Math.round((Number(a || 0) / Number(b || 1)) * 100);
  return `${Math.max(0, Math.min(100, v))}%`;
};
const fmtDate = (msOrIso) => {
  if (!msOrIso) return "—";
  const d = new Date(typeof msOrIso === "number" ? msOrIso : msOrIso);
  if (Number.isNaN(d.getTime())) return String(msOrIso);
  return d.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const STATUS_TONE = {
  active:    { bg: "#E6F6EC", fg: "#15803C", label: "Active" },
  scheduled: { bg: "#E8F1FF", fg: "#1E63D6", label: "Scheduled" },
  paused:    { bg: "#FFF3D6", fg: "#9A6700", label: "Paused" },
  expired:   { bg: "#F1F5F9", fg: "#64748B", label: "Expired" },
  draft:     { bg: "#F1F5F9", fg: "#64748B", label: "Draft" },
  archived:  { bg: "#F1F5F9", fg: "#64748B", label: "Archived" },
};

const StatusBadge = ({ status }) => {
  const tone = STATUS_TONE[status] || STATUS_TONE.draft;
  return (
    <span
      className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] font-semibold"
      style={{ background: tone.bg, color: tone.fg }}
    >
      <span className="w-1.5 h-1.5 rounded-full" style={{ background: tone.fg }} />
      {tone.label}
    </span>
  );
};

const KpiCard = ({ label, value, tone = "slate" }) => {
  const tones = {
    slate:  "#0F172A",
    blue:   "#1E63D6",
    green:  "#15803C",
    purple: "#7C3AED",
  };
  return (
    <div className="bg-white border border-[#E6E9EF] rounded-lg px-3.5 py-2.5">
      <div className="text-[11px] text-[#64748B] font-medium">{label}</div>
      <div
        className="text-[22px] font-extrabold leading-tight mt-0.5 tabular-nums"
        style={{ color: tones[tone] }}
      >
        {value}
      </div>
    </div>
  );
};

const Progress = ({ value, color }) => (
  <div className="h-2 bg-[#F1F5F9] rounded-full overflow-hidden">
    <div
      className="h-full rounded-full transition-all"
      style={{ width: value, background: color }}
    />
  </div>
);

const ConfigRow = ({ label, value }) => (
  <div className="flex items-center justify-between px-4 py-2.5 border-b border-[#EEF1F5] last:border-b-0">
    <span className="text-[12.5px] text-[#64748B]">{label}</span>
    <span className="text-[12.5px] font-semibold text-[#0F172A] text-right">
      {value}
    </span>
  </div>
);

const CodeRow = ({ code, status }) => {
  const tone = STATUS_TONE[status] ||
    { bg: "#F1F5F9", fg: "#475569", label: status || "Available" };
  return (
    <div className="flex items-center gap-2 bg-white border border-[#E6E9EF] rounded-md px-3 py-2.5">
      <span className="font-mono text-[13px] font-semibold tracking-wider text-[#0F172A] flex-1 truncate">
        {code}
      </span>
      <span
        className="inline-flex items-center px-2 py-0.5 rounded-full text-[10.5px] font-semibold capitalize"
        style={{ background: tone.bg, color: tone.fg }}
      >
        {status}
      </span>
      <button
        type="button"
        title="Copy"
        onClick={() => {
          try {
            navigator.clipboard.writeText(code);
            successToast(`Copied ${code}`);
          } catch {
            errorToast("Copy failed");
          }
        }}
        className="text-[#94A3B8] hover:text-[#0F172A] p-1"
      >
        <Copy size={13} />
      </button>
    </div>
  );
};

// Render the discount payload as a one-line summary the cashier can scan.
const discountText = (c) => {
  const d = c.config?.discount;
  const t = c.discount_type;
  if (t === "percent") {
    const pct = d?.value ?? 0;
    return d?.max_cap ? `${pct}% (cap ${fmtINR(d.max_cap)})` : `${pct}% off`;
  }
  if (t === "flat") return `${fmtINR(d?.value)} off`;
  if (t === "bogo")
    return `Buy ${d?.buy_qty} get ${d?.get_qty} ${d?.get_type === "half" ? "@ 50%" : "free"}`;
  if (t === "tiered") return `${(d?.slabs || []).length} slabs`;
  return t || "—";
};

const maxCapText = (c) => {
  const d = c.config?.discount;
  if (c.discount_type === "percent" && d?.max_cap != null) return fmtINR(d.max_cap);
  return "—";
};

const codeFormatText = (c) => {
  if (c.series_id) {
    const prefix = c.config?.series?.prefix || "—";
    const len =
      Number(c.config?.series?.generation_config?.code_length ?? 5) || 5;
    return `${prefix}${"X".repeat(len)}`;
  }
  return c.single_code || "—";
};

const usesPerCodeText = (c) => {
  const u = c.config?.limits?.uses_per_code;
  if (u == null) return "1 use";
  return `${u} use${u === 1 ? "" : "s"}`;
};

const CouponDetailPanel = ({ coupon, onClose, onEdit }) => {
  const [codes, setCodes] = useState([]);
  const [codesLoading, setCodesLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  const isSeries = !!coupon?.series_id;
  const generated = Number(coupon?.series_generated ?? coupon?.codes_count ?? 0);
  const target = Number(coupon?.series_target ?? generated);
  const used = Number(coupon?.used_count ?? 0);
  // Without a separate "issued vs available" column, we treat any code not
  // status='available' as issued.
  const issued = codes.filter((c) => c.status !== "available").length || used;

  // Lazy-load a small sample on mount (series only — single has 1 code which
  // is already in coupon.single_code).
  useEffect(() => {
    if (!coupon || !isSeries) {
      setCodes([]);
      return;
    }
    let cancelled = false;
    setCodesLoading(true);
    api
      .get(`/coupons/${encodeURIComponent(coupon.id)}/codes?limit=20`)
      .then((res) => {
        if (cancelled) return;
        setCodes(res?.data?.data?.rows || []);
      })
      .catch((err) => {
        if (cancelled) return;
        console.warn("[CouponDetailPanel] codes fetch failed:", err?.message);
        setCodes([]);
      })
      .finally(() => !cancelled && setCodesLoading(false));
    return () => { cancelled = true; };
  }, [coupon?.id, isSeries]);

  const exportCsv = async () => {
    if (!coupon) return;
    setExporting(true);
    try {
      // We don't go through axios so we can stream the bytes straight to a
      // blob — the existing api instance would JSON-parse the body otherwise.
      const token = localStorage.getItem("token");
      const res = await fetch(
        `${api.defaults.baseURL || ""}/coupons/${encodeURIComponent(coupon.id)}/codes?format=csv`,
        { headers: { authorization: token || "" } }
      );
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `coupon-${coupon.id}-codes.csv`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      successToast("CSV exported");
    } catch (err) {
      errorToast(err?.message || "Export failed");
    } finally {
      setExporting(false);
    }
  };

  if (!coupon) return null;

  return (
    <div
      className="fixed inset-0 z-50 flex justify-end"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
    >
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Panel */}
      <div className="relative bg-white w-full sm:w-[460px] h-full overflow-y-auto shadow-2xl flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-white z-10 px-5 pt-4 pb-3 border-b border-[#EEF1F5]">
          <div className="flex items-start gap-2 mb-1.5">
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-[16px] font-bold text-[#0F172A] truncate">
                  {coupon.name}
                </h2>
                <StatusBadge status={coupon.status} />
              </div>
              <div className="text-[11.5px] text-[#64748B]">
                {isSeries ? "Series" : "Single code"} · created {fmtDate(coupon.created_at)}
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="text-[#64748B] hover:text-[#0F172A] p-1.5 rounded hover:bg-[#F1F5F9]"
              title="Close"
            >
              <X size={16} />
            </button>
          </div>
        </div>

        <div className="px-5 py-4 space-y-4 flex-1">
          {/* KPI strip */}
          {isSeries ? (
            <div className="grid grid-cols-3 gap-2.5">
              <KpiCard label="Generated" value={fmtNum(generated)} />
              <KpiCard label="Issued" value={fmtNum(issued)} tone="blue" />
              <KpiCard label="Redeemed" value={fmtNum(used)} tone="green" />
            </div>
          ) : (
            <div className="grid grid-cols-2 gap-2.5">
              <KpiCard
                label="Code"
                value={
                  <span className="font-mono text-[15px]">
                    {coupon.single_code || "—"}
                  </span>
                }
              />
              <KpiCard label="Redeemed" value={fmtNum(used)} tone="green" />
            </div>
          )}

          {/* Progress bars (series only) */}
          {isSeries && (
            <div className="bg-white border border-[#E6E9EF] rounded-lg p-3.5 space-y-3">
              <div>
                <div className="flex justify-between text-[11.5px] mb-1">
                  <span className="text-[#64748B]">Issued of generated</span>
                  <span className="font-bold text-[#0F172A]">{fmtPct(issued, generated)}</span>
                </div>
                <Progress value={fmtPct(issued, generated)} color="#1E63D6" />
              </div>
              <div>
                <div className="flex justify-between text-[11.5px] mb-1">
                  <span className="text-[#64748B]">Redeemed of issued</span>
                  <span className="font-bold text-[#0F172A]">{fmtPct(used, issued)}</span>
                </div>
                <Progress value={fmtPct(used, issued)} color="#15803C" />
              </div>
              <div className="flex justify-between text-[11.5px] pt-1 border-t border-[#EEF1F5]">
                <span className="text-[#64748B]">Remaining (un-issued)</span>
                <span className="font-bold text-[#0F172A] tabular-nums">
                  {fmtNum(Math.max(0, generated - issued))}
                </span>
              </div>
            </div>
          )}

          {/* Configuration */}
          <div>
            <div className="text-[10.5px] font-bold uppercase tracking-wider text-[#64748B] mb-1.5 px-1">
              Configuration
            </div>
            <div className="bg-white border border-[#E6E9EF] rounded-lg">
              <ConfigRow label="Code format" value={<span className="font-mono">{codeFormatText(coupon)}</span>} />
              <ConfigRow label="Uses per code" value={usesPerCodeText(coupon)} />
              <ConfigRow label="Discount" value={discountText(coupon)} />
              <ConfigRow label="Max discount" value={maxCapText(coupon)} />
              <ConfigRow
                label="Min. purchase"
                value={coupon.config?.limits?.min_purchase ? fmtINR(coupon.config.limits.min_purchase) : "—"}
              />
              <ConfigRow
                label="Validity"
                value={`${fmtDate(coupon.valid_from)} – ${fmtDate(coupon.valid_to)}`}
              />
              <ConfigRow
                label="Sold items returnable"
                value={
                  coupon.config?.return_policy?.non_returnable ? (
                    "No"
                  ) : (
                    <span className="inline-flex items-center gap-1 text-[#15803C]">
                      <Check size={12} /> Yes
                    </span>
                  )
                }
              />
            </div>
          </div>

          {/* Sample codes (series only) */}
          {isSeries && (
            <div>
              <div className="flex items-center justify-between mb-1.5 px-1">
                <span className="text-[10.5px] font-bold uppercase tracking-wider text-[#64748B]">
                  Sample codes
                </span>
                <span className="text-[10.5px] text-[#94A3B8]">
                  {fmtNum(generated)} total
                </span>
              </div>
              {codesLoading ? (
                <div className="text-[12px] text-[#64748B] py-3 flex items-center gap-2">
                  <Loader2 size={14} className="animate-spin" /> Loading codes…
                </div>
              ) : codes.length === 0 ? (
                <div className="text-[12px] text-[#94A3B8] py-3">No codes generated yet.</div>
              ) : (
                <div className="space-y-1.5">
                  {codes.slice(0, 3).map((c) => (
                    <CodeRow key={c.id} code={c.code} status={c.status} />
                  ))}
                  {codes.length > 3 && (
                    <div className="text-center text-[11.5px] text-[#64748B] pt-1">
                      + {fmtNum(generated - 3)} more codes
                    </div>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* Footer actions */}
        <div className="sticky bottom-0 bg-white border-t border-[#EEF1F5] px-5 py-3 flex items-center gap-2">
          {isSeries && (
            <button
              type="button"
              onClick={exportCsv}
              disabled={exporting}
              className="flex items-center gap-1.5 px-3 py-2 text-[12.5px] font-semibold text-[#0F172A] bg-white border border-[#E6E9EF] rounded-md hover:bg-[#F8FAFC] disabled:opacity-50"
            >
              {exporting ? <Loader2 size={13} className="animate-spin" /> : <Download size={13} />}
              Export CSV
            </button>
          )}
          <div className="flex-1" />
          {isAdmin?<button
            type="button"
            onClick={() => onEdit?.(coupon)}
            className="flex items-center gap-1.5 px-3.5 py-2 text-[12.5px] font-bold text-white bg-primary rounded-md hover:opacity-90"
          >
            <Pencil size={13} /> Edit
          </button>:<></>}
        </div>
      </div>
    </div>
  );
};

export default CouponDetailPanel;
